import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { ProgressBarModule } from 'primeng/progressbar';
import { TagModule } from 'primeng/tag';
import { forkJoin } from 'rxjs';
import { PageDetailHeroComponent } from '../../shared/page-layout/page-detail-hero.component';
import { PageFormSectionComponent } from '../../shared/page-layout/page-form-section.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { CrmAccount, CrmClient } from '../../crm/crm.model';
import { CrmService } from '../../crm/crm.service';
import { billingChargeTypeLabel, billingChargeUnitLabel, masterDataMap, statusSeverity, warehousingBreadcrumb, warehousingHomeBreadcrumb } from '../warehousing-common';
import { WarehousingStorageRate } from '../warehousing.model';
import { WarehousingService } from '../warehousing.service';

@Component({
  selector: 'app-warehousing-storage-rate-view',
  standalone: true,
  imports: [CommonModule, RouterModule, BreadcrumbModule, ButtonModule, ProgressBarModule, TagModule, PageDetailHeroComponent, PageFormSectionComponent, PageMetricStripComponent, PageShellComponent],
  templateUrl: './warehousing-storage-rate-view.component.html'
})
export class WarehousingStorageRateViewComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private warehousingService = inject(WarehousingService);
  private crmService = inject(CrmService);
  isLoading = false;
  rate?: WarehousingStorageRate;
  clients: CrmClient[] = [];
  accounts: CrmAccount[] = [];
  masterData = masterDataMap();
  breadcrumbItems: MenuItem[] = warehousingBreadcrumb('Storage Rate Details', 'Storage Rates', '/warehousing/setup/storage-rates');
  homeBreadcrumb: MenuItem = warehousingHomeBreadcrumb();

  ngOnInit(): void {
    const id = Number(this.route.snapshot.paramMap.get('id'));
    this.isLoading = true;
    forkJoin({
      rates: this.warehousingService.getStorageRates(),
      clients: this.crmService.getClients(),
      accounts: this.crmService.getAccounts(),
      regions: this.warehousingService.getMasterData('REGION'),
      warehouses: this.warehousingService.getMasterData('WAREHOUSE')
    }).subscribe({
      next: response => {
        this.rate = (response.rates.data || []).find(row => row.id === id);
        this.clients = response.clients || [];
        this.accounts = response.accounts || [];
        this.masterData.REGION = response.regions.data || [];
        this.masterData.WAREHOUSE = response.warehouses.data || [];
        this.isLoading = false;
      },
      error: () => this.isLoading = false
    });
  }

  clientName(id?: number): string { return this.clients.find(client => client.id === id)?.companyName || '-'; }
  accountName(id?: number): string { return this.accounts.find(account => account.id === id)?.companyName || '-'; }
  masterName(type: 'REGION' | 'WAREHOUSE', id?: number): string {
    return this.masterData[type].find(item => item.id === id)?.name || '-';
  }

  metrics(): PageMetric[] {
    return [
      { label: 'Charge Type', value: this.chargeTypeLabel(this.rate?.chargeType || this.rate?.metric) },
      { label: 'Charge Unit', value: this.chargeUnitLabel(this.rate?.chargeUnit) },
      { label: 'Rate', value: `${this.formatRate(this.rate?.ratePerDay)} ${this.rate?.currency || ''}`.trim(), tone: 'info' },
      { label: 'Status', value: this.rate?.isActive ? 'ACTIVE' : 'INACTIVE', tone: this.rate?.isActive ? 'success' : 'danger' }
    ];
  }

  statusSeverity = statusSeverity;
  chargeTypeLabel = billingChargeTypeLabel;
  chargeUnitLabel = billingChargeUnitLabel;

  private formatRate(value?: number): string {
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: 4, minimumFractionDigits: 2 }).format(value || 0);
  }
}
